import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { DateTime } from 'luxon';
import { EidosBaseComponent } from '../eidos-base.component';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';

type DateSelectorGridData<T> = { [day: number]: T, month: string }
export type DateSelectorGridDateRange = { startDate: DateTime | undefined; endDate: DateTime | undefined | null } // endDate can be null if the user press SHIFT, so the range doesn't accept the endDate
type DateSelectorGridUtilities = { selected: boolean, inRange: boolean, changed: boolean, available: boolean }

enum DateSelectorGridCellMode {
  Top = 'top',
  TopAndBottom = 'topAndBottom',
}

@Component({
    selector: 'eidos-date-selector-grid',
    templateUrl: './eidos-date-selector-grid.component.html',
    styleUrls: ['./eidos-date-selector-grid.component.scss'],
    standalone: false
})

export class EidosDateSelectorGridComponent<T> extends EidosBaseComponent {
  DateSelectorGridCellMode = DateSelectorGridCellMode
  @ViewChild('calendarDataGrid', { static: false }) calendarDataGrid?: DxDataGridComponent;

  days: number[] = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31,
  ];
  months: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  @Input() year: number = new Date().getFullYear()

  @Input() dateField: keyof T = 'date' as keyof T;
  @Input() topField?: keyof T
  @Input() bottomField?: keyof T
  @Input() tooltip?: keyof T
  @Input() checkInDay?: number //1: Monday, 2: Tuesday, 3: Wednesday, 4: Thursday, 5: Friday, 6: Saturday, 7: Sunday
  @Input() bookedDays?: DateTime[]
  @Input() statusColors:{status:string,bg:string,fg:string,fs:string}[]=[]
  @Input() statusField?: keyof T
  @Input() legendExt:{char:string,desc:string}[]=[];
  @Input() customLegend: {class: string, label: string}[]=[];
  private _validateDate: (date: DateTime | undefined) => boolean = (date) => !!date?.isValid;
  get validateDate(): (date: DateTime | undefined) => boolean {
    return this._validateDate;
  }
  @Input()
  set validateDate(value: (date: DateTime | undefined) => boolean) {
    if (value) this._validateDate = (date) => !!date?.isValid && value(date)
  }

  @Input() enableWeekdayName = false
  @Input() multiRange: boolean = false;
  @Input() oneMoreMonth: boolean = false;
  @Input() disabled: boolean = false;
  @Input() ifTopAndBottomValuesAreEqualsShowOnlyOne: boolean = false;

  skipEventEmit=false
  private _selectedDateRanges: DateSelectorGridDateRange[] = [];
  get selectedDateRanges(): DateSelectorGridDateRange[] {
    return this._selectedDateRanges;
  }
  @Input() set selectedDateRanges(value: DateSelectorGridDateRange[]) {
    this._selectedDateRanges = value;
    this.buildCellColor()
    if(this.skipEventEmit) {
      this.skipEventEmit=false
      return
    }
    this.selectedDateRangesChange.emit(value);
    this.selectedDatesChange.emit(this.allSelectedDays);
  }
  @Output() selectedDateRangesChange = new EventEmitter<DateSelectorGridDateRange[]>();
  @Output() selectedDatesChange = new EventEmitter<DateTime[]>();

  get allSelectedDays(): DateTime[] {
    return this.selectedDateRanges.reduce((acc, curr) => {
      if (curr.startDate && curr.endDate) {
        return [...acc, ...this.buildDateTimeRange(curr.startDate, curr.endDate)]
      }
      if (curr.startDate) {
        return [...acc, curr.startDate]
      }
      return acc;
    }, [] as DateTime[]);
  }

  get allSelectedData(): T[] {
    return this._dataSource.filter(d => this.allSelectedDays.some(sd => this.areDateTimeEqual(sd, (d[this.dateField] as unknown as DateTime))))
  }

  dataSourceUtilities: { [dateTimeMillis: string]: DateSelectorGridUtilities } = {}

  protected structuredDataSource: DateSelectorGridData<T>[] = this.getStrucuturedDataSource([])
  private _dataSource: T[] = [];
  @Input() set dataSource(value: T[]) {
    this._dataSource = value;
    this.structuredDataSource = this.getStrucuturedDataSource(value);
    this.buildCellColor()
    this.hasAnyOverlay = value.some((el:any)=>el.overlay);
  }
  hasAnyOverlay: boolean = false;

  constructor() {
    super();
  }

  ngOnInit(): void {
  }

  refreshCalendar() {
    this.structuredDataSource = this.getStrucuturedDataSource(this._dataSource);
    this.buildCellColor()

  }
  setSelection(selections:DateSelectorGridDateRange[]) {
    this.selectedDateRanges = selections
  }
  getCellMode(cellData: T): DateSelectorGridCellMode {
    if (this.topField && this.bottomField && (!this.ifTopAndBottomValuesAreEqualsShowOnlyOne || cellData[this.topField] != cellData[this.bottomField])) return DateSelectorGridCellMode.TopAndBottom
    return DateSelectorGridCellMode.Top
  }

  onCellPrepared(e: any) {
    if (e.rowType == 'data') {
      this.isWeekend(e) && e.cellElement.classList.add('we');

      if(this.checkInDay && this.getCellData(e)?.date?.weekday === this.checkInDay){
        e.cellElement.classList.add('check-in-day');
      }

      if(this.bookedDays?.some(d => this.areDateTimeEqual(d, this.getCellData(e)?.date))){
        e.cellElement.classList.add('booked-day');
      }

      e.columnIndex === 0 && e.cellElement.classList.add('cell-month', 'border-none');
    }
  }

  onCellClick(e: any) {
    const selectedDate = e.data?.[e.column.dataField]?.[this.dateField]

    if (this.disabled
        || e.columnIndex === 0
        || e.rowType !== 'data'
        || !selectedDate
        || !this.dataSourceUtilities[+selectedDate]?.available
        || (this.checkInDay
            && this.checkInDay !== 8
            && this.getCellData(e)?.date?.weekday !== this.checkInDay
    )) return;

    const { ctrlKey: isCtrlPressed, shiftKey: isShiftPressed } = <KeyboardEvent>window.event

    if (!this.multiRange || (!isCtrlPressed && !isShiftPressed)) {

      if (this.selectedDateRanges.length === 0 || this.selectedDateRanges.length > 1 || (this.selectedDateRanges[0].startDate && (this.selectedDateRanges[0].endDate || this.selectedDateRanges[0].startDate > selectedDate))) {
        this.selectedDateRanges = [{ startDate: selectedDate, endDate: undefined }]
        return
      }

      if (this.areDateTimeEqual(this.selectedDateRanges[0].startDate, selectedDate)) {
        this.clearSelection()
        return
      }

      this.selectedDateRanges = [{ startDate: this.selectedDateRanges[0].startDate, endDate: selectedDate }]
      return
    }

    if (this.selectedDateRanges.some(r => !r.endDate && this.areDateTimeEqual(r.startDate, selectedDate))) {
      this.selectedDateRanges = this.selectedDateRanges.filter(r => !this.areDateTimeEqual(r.startDate, selectedDate))
      return
    }

    const lastSelectedDateRange = this.selectedDateRanges.at(-1);

    if (!isShiftPressed && lastSelectedDateRange?.startDate && lastSelectedDateRange.endDate === undefined && selectedDate > lastSelectedDateRange.startDate) {
      const newRange = { startDate: lastSelectedDateRange.startDate, endDate: selectedDate }

      this.selectedDateRanges = [...this.selectedDateRanges.filter(r => !this.isDateTimeInRangeOrStartOrEnd(r.startDate, newRange) && !this.isDateTimeInRangeOrStartOrEnd(r.endDate, newRange)), newRange]
      return
    }

    this.selectedDateRanges = [...this.selectedDateRanges.filter(r => !this.isDateTimeInRangeOrStartOrEnd(selectedDate, r)), { startDate: selectedDate, endDate: isShiftPressed ? null : undefined }]
  }

  isWeekend(e: any) {
    const data = e.data[e.column.dataField];
    if (!data) return false;
    return [6, 7].includes(data.date?.weekday)
  }

  areDateTimeEqual(date1: DateTime | undefined | null, date2?: DateTime | undefined | null) {
    if (!date1 || !date2) return false;
    return +date1 === +date2;
  }

  buildDateTimeRange(d1?: DateTime, d2?: DateTime) {
    if (!d1 && !d2) return [];
    if (!d1) return [d2] as DateTime[];
    if (!d2) return [d1] as DateTime[];
    let days = d2.diff(d1, 'days').days;
    let dateRange = [];
    for (let i = 0; i <= days; i++) {
      dateRange.push(d1.plus({ days: i }));
    }
    return dateRange;
  }
  getWeekDay(cellData: T){
    if(!this.enableWeekdayName) return ''
    const sep = this.tooltip && (cellData[this.tooltip] ?? '').toString().trim() != '' ?  ' - ' : ''
    const dt = DateTime.fromMillis(+cellData[this.dateField])
    const wd = dt.weekdayShort
    const dtFmt = dt.toFormat('dd LLL yyyy')
    return `${wd} ${dtFmt}${sep}`
  }
  getStrucuturedDataSource(data: T[]): DateSelectorGridData<T>[] {
    let structuredDataSource: DateSelectorGridData<T>[] = [];
    this.dataSourceUtilities = {};

    if(this.oneMoreMonth && this.months.length===12) this.months.push(this.months[0])
    for (let [i, m] of this.months.entries()) {
      let monthData: DateSelectorGridData<T> = { month: m.slice(0, 3) }
      for (let d of this.days) {
        let month = i+1
        let year = this.year
        if(month>12) {
          month -= 12
          year += 1
        }
        let dayData = data.find((x: any) => x[this.dateField]?.year === year && x[this.dateField]?.month === month && x[this.dateField].day === d);
        // if in the data source there is no data for the current day, create a new object with the current date
        if (!dayData) dayData = { [this.dateField]: DateTime.fromObject({ year: year, month: month, day: d }) } as unknown as T;

        this.dataSourceUtilities[this.getCellDataDateKey(dayData)] = { available: this.validateDate(dayData[this.dateField] as unknown as DateTime), selected: false, inRange: false, changed: false }

        monthData[d] = dayData;
      }
      structuredDataSource.push(monthData);
    }
    return structuredDataSource;
  }

  getCellData(data: any) {
    return data.data[data.column.dataField]
  }

  isCellSelected(date: DateTime) {
    if(this.multiRange) {
      return !!this.selectedDateRanges.find(r => this.isDateTimeStartOrEndOfRange(date, r))
    }
    return this.selectedDateRanges.some(r => this.isDateTimeStartOrEndOfRange(date, r))
  }

  isCellInRange(date: DateTime) {
    if(this.multiRange) {
      return !!this.selectedDateRanges.find(r => this.isDateTimeInRange(date, r))
    }
    return this.selectedDateRanges.some(r => this.isDateTimeInRange(date, r))
  }

  isDateTimeStartOrEndOfRange(date: DateTime | undefined | null, range: DateSelectorGridDateRange) {
    return date && (this.areDateTimeEqual(range.startDate, date) || this.areDateTimeEqual(range.endDate, date))
  }

  isDateTimeInRange(date: DateTime | undefined | null, range: DateSelectorGridDateRange) {
    return date && range.startDate && range.endDate && range.startDate < date && date < range.endDate
  }

  isDateTimeInRangeOrStartOrEnd(date: DateTime | undefined | null, range: DateSelectorGridDateRange) {
    return this.isDateTimeStartOrEndOfRange(date, range) || this.isDateTimeInRange(date, range)
  }

  isDateTimeAvailable(date: DateTime) {
    return this.dataSourceUtilities[this.getCellDataDateKey({ [this.dateField]: date } as unknown as T)]?.available;
  }

  buildCellColor() {
    for (const key in this.dataSourceUtilities) {
      const value = this.dataSourceUtilities[key];
      const dtKey = DateTime.fromMillis(+key)
      value.selected = this.isCellSelected(dtKey);
      value.inRange = !(value.selected) ? this.isCellInRange(dtKey) : false;
    }
  }

  checkCellFlag(cellData: T, flag: 'selected' | 'inRange' | 'available') {
    const sc = this.getStatusColor(cellData)
    if(!!sc) return false
    return this.dataSourceUtilities[this.getCellDataDateKey(cellData)]?.[flag];
  }
  isAvailable(cellData: T) {
    const c = this.dataSourceUtilities[this.getCellDataDateKey(cellData)]
    if(!c) return false
    return c['available'];
  }
  getStatusColor(cellData: T) {
    if(this.statusColors.length===0) return undefined
    if(!this.statusField) return undefined
    const v =  cellData[this.statusField]
    const s = this.statusColors.find(sc=>sc.status===v)
    if(!s) return undefined
    return {
      'background-color': s.bg,
      'color': s.fg,
      'font-size': s.fs
    }
  }
  getCellDataDateKey(cellData: T) {
    return (+cellData[this.dateField]).toString();
  }

  // from outside
  setFirstRange(startDate: DateTime | undefined, endDate: DateTime | undefined) {
    if (!startDate?.isValid && endDate?.isValid) return;
    if (this.selectedDateRanges[0]?.startDate == startDate && this.selectedDateRanges[0]?.endDate == endDate) return

    if ((startDate?.isValid && !this.isDateTimeAvailable(startDate)) || (endDate?.isValid && !this.isDateTimeAvailable(endDate))) {
      this.clearSelection();
      return
    }

    if(!this.multiRange) {
      this.selectedDateRanges = [{ startDate, endDate }]
    }
  }
  addRanges(travelPeriod:{dateStart: DateTime, dateEnd: DateTime, travelDateID?: number}[]) {
    this.selectedDateRanges = this.selectedDateRanges.concat(
                                travelPeriod.filter(p=>p.dateStart && p.dateStart.isValid).map(p=>{
                                  return {
                                    startDate:p.dateStart,
                                    endDate:p.dateEnd && p.dateEnd.isValid ? p.dateEnd : p.dateStart,
                                    travelDateID: p.travelDateID,
                                  }
                                }))
    this.calendarDataGrid?.instance.refresh()
  }
  removeRanges(travelPeriod:{dateStart: DateTime, dateEnd: DateTime}[]){
    this.selectedDateRanges = this.selectedDateRanges
                                .filter(tp=>
                                  tp.startDate && tp.endDate &&
                                  !travelPeriod.find(p=>p.dateStart.equals(tp.startDate!) && p.dateEnd.equals(tp.endDate!)))
    this.calendarDataGrid?.instance.refresh()
  }
  setRanges(travelPeriod:{dateStart?: DateTime | undefined, dateEnd?: DateTime | undefined, travelDateID?: number}[]) {
    this.selectedDateRanges = travelPeriod.filter(p=>p.dateStart && p.dateStart.isValid).map(p=>{
                              return {
                                startDate:p.dateStart,
                                endDate:p.dateEnd && p.dateEnd.isValid ? p.dateEnd : p.dateStart,
                                travelDateID: p.travelDateID,
                              }
                            })
    this.calendarDataGrid?.instance.refresh()
  }
  setRanges2(travelPeriod:DateSelectorGridDateRange[]) {
    this.skipEventEmit=true
    this.selectedDateRanges = travelPeriod.filter(p=>p.startDate && p.startDate.isValid).map(p=>{
                              return {
                                startDate:p.startDate,
                                endDate:p.endDate && p.endDate.isValid ? p.endDate : p.startDate,
                              }
                            })
    this.calendarDataGrid?.instance.refresh()
  }
  selectAllYear() {
    const dateStart = Object.keys(this.dataSourceUtilities).find((x) => this.dataSourceUtilities[x].available);
    const dateEnd = Object.keys(this.dataSourceUtilities).reverse().find((x) => this.dataSourceUtilities[x].available);

    if (dateStart && dateEnd) this.selectedDateRanges = [{ startDate: DateTime.fromMillis(+dateStart), endDate: DateTime.fromMillis(+dateEnd) }]
  }

  clearSelection() {
    this.selectedDateRanges = [];
  }
}
